/**
 * Driver Status List Component
 * List of drivers with live status
 */

import { Card, Badge } from '../ui';
import { User, MapPin, Clock, Navigation } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { DriverWithPosition } from '../../types';

interface DriverStatusListProps {
  drivers: DriverWithPosition[];
  selectedDriverUid?: string;
  onSelectDriver: (driverUid: string) => void;
  isLoading?: boolean;
}

// Get status based on position age
const getStatus = (recordedAt: string): { label: string; variant: 'success' | 'warning' | 'default' | 'danger' } => {
  const ageMinutes = (Date.now() - new Date(recordedAt).getTime()) / 60000;
  if (ageMinutes < 5) return { label: 'Active', variant: 'success' };
  if (ageMinutes < 15) return { label: 'Recent', variant: 'warning' };
  if (ageMinutes < 60) return { label: 'Stale', variant: 'default' };
  return { label: 'Offline', variant: 'danger' };
};

// Format time ago
const formatTimeAgo = (dateStr: string): string => {
  const ageMinutes = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (ageMinutes < 1) return 'Just now';
  if (ageMinutes < 60) return `${ageMinutes}m ago`;
  const hours = Math.floor(ageMinutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

export default function DriverStatusList({
  drivers,
  selectedDriverUid,
  onSelectDriver,
  isLoading,
}: DriverStatusListProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <Card key={i} padding="sm">
            <div className="animate-pulse flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded-full" />
              <div className="flex-1">
                <div className="h-4 w-32 bg-gray-200 rounded mb-2" />
                <div className="h-3 w-24 bg-gray-200 rounded" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (drivers.length === 0) {
    return (
      <Card>
        <div className="text-center py-8">
          <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No drivers</h3>
          <p className="text-gray-500 text-sm">
            No drivers with recent positions found.
          </p>
        </div>
      </Card>
    );
  }

  // Sort by most recent first
  const sortedDrivers = [...drivers].sort(
    (a, b) => new Date(b.recorded_at).getTime() - new Date(a.recorded_at).getTime()
  );

  return (
    <div className="space-y-2">
      {sortedDrivers.map((driver) => {
        const isSelected = driver.driver_uid === selectedDriverUid;
        const status = getStatus(driver.recorded_at);

        return (
          <Card
            key={driver.driver_uid}
            padding="sm"
            className={cn(
              'cursor-pointer transition-all hover:shadow-md',
              isSelected && 'ring-2 ring-blue-500 bg-blue-50'
            )}
            onClick={() => onSelectDriver(driver.driver_uid)}
          >
            <div className="flex items-center gap-3">
              {/* Avatar with status indicator */}
              <div className="relative">
                <div
                  className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center',
                    status.variant === 'success' && 'bg-green-100',
                    status.variant === 'warning' && 'bg-yellow-100',
                    status.variant === 'default' && 'bg-gray-100',
                    status.variant === 'danger' && 'bg-red-100'
                  )}
                >
                  <User
                    className={cn(
                      'h-5 w-5',
                      status.variant === 'success' && 'text-green-600',
                      status.variant === 'warning' && 'text-yellow-600',
                      status.variant === 'default' && 'text-gray-400',
                      status.variant === 'danger' && 'text-red-600'
                    )}
                  />
                </div>
                {/* Pulse indicator for active drivers */}
                {status.variant === 'success' && (
                  <span className="absolute -top-0.5 -right-0.5 w-3 h-3">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
                  </span>
                )}
              </div>

              {/* Driver Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2">
                  {/* Wraps instead of cutting: dispatch needs the whole name (fleet feedback 2026-11-03) */}
                  <span dir="auto" className="font-medium text-gray-900 break-words min-w-0">
                    {driver.full_name}
                  </span>
                  <Badge variant={status.variant} size="sm" className="shrink-0">
                    {status.label}
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 mt-0.5 whitespace-nowrap">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatTimeAgo(driver.recorded_at)}
                  </span>
                  {driver.source === 'tap' && (
                    <span title="Position is from the driver's last task action in the app, not from live GPS.">
                      from last task
                    </span>
                  )}
                  {driver.speed !== undefined && driver.speed > 0 && (
                    <span className="flex items-center gap-1">
                      <Navigation className="h-3 w-3" />
                      {driver.speed.toFixed(0)} km/h
                    </span>
                  )}
                </div>
              </div>

              {/* Location */}
              {driver.lat != null && driver.lng != null && (
                <div className="text-right text-xs text-gray-500">
                  <div className="flex items-center gap-1 justify-end">
                    <MapPin className="h-3 w-3" />
                    <span>{driver.lat.toFixed(4)}</span>
                  </div>
                  <span>{driver.lng.toFixed(4)}</span>
                </div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
